import type { ClientTheme } from "@/app/lib/themes"

/**
 * Shared style utilities for themed filter controls (inputs, selects).
 * Used across InventoryTable, TransactionsPage, and ORRIPage.
 *
 * Colours must remain as inline styles because they reference runtime theme
 * values — Tailwind classes are compiled at build time and cannot encode them.
 */

export function controlBase(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    color: theme.textPrimary,
    backgroundColor: theme.bgSurface,
    border: `1px solid ${theme.borderColor}`,
    borderRadius: "8px",
    outline: "none",
    height: "34px",
    transition: "border-color 150ms ease, box-shadow 150ms ease",
  }
}

/**
 * Sticky header cell for in-card scrolling tables. Each th carries its own
 * background (a tr background would scroll away once the header sticks) and
 * an inset shadow standing in for the border-bottom, which border-collapse
 * drops from stuck cells.
 */
export function stickyTh(theme: ClientTheme): React.CSSProperties {
  return {
    position: "sticky",
    top: 0,
    zIndex: 5,
    backgroundColor: theme.bgSurface2,
    boxShadow: `inset 0 -1px 0 ${theme.borderColor}`,
  }
}

/**
 * Shrink-to-fit column hint (the classic 1%-width trick): the column hugs its
 * widest cell instead of taking a share of surplus width. Mark every compact
 * column with this so the one column left on auto absorbs the extra space on
 * wide viewports.
 */
export const fitCol: React.CSSProperties = { width: "1%", whiteSpace: "nowrap" }

export function onFocus(theme: ClientTheme) {
  return (e: React.FocusEvent<HTMLElement>) => {
    e.currentTarget.style.borderColor = theme.brandPrimary
    e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.brandPrimary}18`
  }
}

export function onBlur(theme: ClientTheme) {
  return (e: React.FocusEvent<HTMLElement>) => {
    e.currentTarget.style.borderColor = theme.borderColor
    e.currentTarget.style.boxShadow = "none"
  }
}
